import {computed} from 'vue';

import {ESettingsFrontType} from './enums';

import type {ISettings} from './types';
import type {ISettingsMapTilePreset} from './types';

export const settings: ISettings = {
  APP: {
    VERSION: import.meta.env.VITE_APP_VERSION ?? __APP_VERSION__ ?? 'unknown',
    NAME: __APP_NAME__ ?? 'unknown',
    TEST: import.meta.env.TEST === 'true',
  },
  SERVICES: {
    KEYCLOAK: {
      URL: import.meta.env.VITE_KEYCLOAK_URL ?? 'https://keycloak.sw.kbsynergya.ru',
      REALM:
        import.meta.env.VITE_KEYCLOAK_REALM ?? 'rburym',
      CLIENT_ID:
        import.meta.env.VITE_KEYCLOAK_CLIENT_ID ??
        'atlas_frontend',
      CLIENT_ID_ROLES:
        import.meta.env.VITE_KEYCLOAK_CLIENT_ID_ROLES ??
        'atlas_backend',
    },

    NOTIFIER: {
      URL: import.meta.env.VITE_NOTIFIER_URL ?? 'https://rburym-tetis-frontend.sw.kbsynergya.ru',
      BACKEND_URL:
        import.meta.env.VITE_NOTIFIER_BACKEND_URL ??
        'https://rburym-tetis-backend.sw.kbsynergya.ru/api/v1',
    },

    FRONT: {
      TYPE:
        ((document.querySelector('meta[name="front-type"]') as HTMLMetaElement)
          ?.content as ESettingsFrontType) ?? ESettingsFrontType.CLIENT,
      PRESENTER: (() => {
        const type =
          ((
            document.querySelector('meta[name="front-type"]') as HTMLMetaElement
          )?.content as ESettingsFrontType) ?? ESettingsFrontType.CLIENT;

        const hasAccessByMode = (modes?: ESettingsFrontType[]) => {
          return (modes ?? []).includes(type);
        };

        return {
          isFrontTypeClient: computed(() => type === ESettingsFrontType.CLIENT),
          isFrontTypeAdmin: computed(() => type === ESettingsFrontType.ADMIN),

          hasAccessByMode,
        };
      })(),
      THEME: {},
    },

    BACK: {
      URL: import.meta.env.VITE_BACKEND_URL ?? 'https://rburym-atlas-backend.sw.kbsynergya.ru/api/v1',
      getUrl() {
        if (this.URL.endsWith('__PLACEHOLDER'))
          return `${window.location.origin}/api/v1`;
        return this.URL;
      },
    },

    WEBSOCKET: {
      URL: import.meta.env.VITE_WEBSOCKET_URL ?? 'wss://rburym-atlas-backend.sw.kbsynergya.ru/ws/v1',
      getUrl() {
        if (this.URL.endsWith('__PLACEHOLDER'))
          return `${window.location.origin}/ws/v1`;
        return this.URL;
      },
    },

    EMULATION: {
      UAV_LAYER_ID:
        import.meta.env.VITE_UAV_LAYER_ID ?? '1955287f-2a5b-46c2-8aea-b6539d1d9155',
    },

    GO2RTC: {
      URL: import.meta.env.VITE_GO2RTC_URL ?? '',
      getWebRTCUrl(src: string) {
        if (this.URL.endsWith('__PLACEHOLDER'))
          return `${location.origin}/api/webrtc?src=${src}`;
        return `${this.URL}/api/webrtc?src=${src}`;
      },
      getStreamFrameUrl(src: string) {
        if (this.URL.endsWith('__PLACEHOLDER'))
          return `${location.origin}/api/frame.jpeg?src=${src}`;
        return `${this.URL}/api/frame.jpeg?src=${src}`;
      },
    },

    EVENTHANDLER: {
      URL:
        import.meta.env.VITE_EVENTHANDLER_URL ??
        '',
      getUrl() {
        if (this.URL.endsWith('__PLACEHOLDER'))
          return `${window.location.origin}/api/v1`;
        return this.URL;
      },
    },

    TARVOS: {
      BACKEND_URL:
        import.meta.env.VITE_TARVOS_BACKEND_URL ??
        'https://rburym-tarvos-backend.sw.kbsynergya.ru/api/v1',
      FRONTEND_URL:
        import.meta.env.VITE_TARVOS_FRONTEND_URL ??
        'https://rburym-tarvos-frontend.sw.kbsynergya.ru',
    },

    TETIS: {
      ADMIN_URL:
        import.meta.env.VITE_TETIS_ADMIN_URL ?? '',
    },

    MAP: {
      INITIAL_CENTER:
        import.meta.env.VITE_MAP_INITIAL_CENTER ??
        '55.85784025762233,49.035007953643806',
      INITIAL_ZOOM:
        import.meta.env.VITE_MAP_INITIAL_ZOOM ??
        '15',
      TILE_PRESETS: (() => {
        const defaultPreset: ISettingsMapTilePreset = {
          name: 'Esri Satellite',
          tiles: [
            {
              url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
              minZoom: 3,
              maxZoom: 13,
            },
          ],
        };
        const raw =
          import.meta.env.VITE_MAP_TILE_PRESETS ??
          '';
        if (raw.endsWith('__PLACEHOLDER')) return [defaultPreset];
        try {
          const b64 = raw.replace(/-/g, '+').replace(/_/g, '/');
          const decoded = new TextDecoder().decode(
            Uint8Array.from(atob(b64), (c) => c.charCodeAt(0)),
          );
          const parsed = JSON.parse(decoded);
          if (Array.isArray(parsed) && parsed.length) return parsed;
          return [defaultPreset];
        } catch {
          return [defaultPreset];
        }
      })(),
    },
  },
};

// placeholder to external theme
settings.SERVICES.FRONT.THEME = {};

if (Object.keys(settings.SERVICES.FRONT.THEME).length === 0) {
  settings.SERVICES.FRONT.THEME = {
    // colorPrimary: '#9d0000',
    colorPrimary: '#a30a0a',
    colorPrimaryBg: '#f6efef',

    colorError: '#ff0100',
  };
}

if (settings.SERVICES.MAP.INITIAL_CENTER.endsWith('__PLACEHOLDER')) {
  settings.SERVICES.MAP.INITIAL_CENTER = '56.3926839088633,49.66437482674767';
}
if (settings.SERVICES.MAP.INITIAL_ZOOM.endsWith('__PLACEHOLDER')) {
  settings.SERVICES.MAP.INITIAL_ZOOM = '5';
}

if (settings.APP.TEST) {
  settings.SERVICES.BACK.URL = 'https://mock.com';
}

export default settings;
